import {
  PaginationType,
  TransactionType,
  TransactionTypeLogType,
  UseQueryProps,
} from "@lib/types";
import { Drawer } from "antd";
import { memo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiCreateTransaction,
  apiCreateTransactionLog,
  apiGetTransaction,
  apiUpdateTransaction,
} from "@lib/api/transaction";
import TransactionWorkflow from "./TransactionWorkflow";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { useApi } from "@lib/hooks/APIHooks";
import { diffDeep } from "@lib/helpers/ObjectHelper";
import _ from "lodash";

type Props = {
  open: boolean;
  toggle: (status?: boolean) => void;
  selected?: TransactionType;
  handleSelect: (data: TransactionType) => void;
  query?: UseQueryProps<PaginationType<TransactionType>>;
};

function PaymentHistoryDrawer(props: Props) {
  const { messages } = useIntl();
  const { selected, open, toggle, handleSelect, query } = props;
  // const { user } = useTypedSelector((state) => state.auth);
  const { queriesCacheClear } = useApi();

  const { mutateAsync: createTransactionLog } = useMutation({
    mutationFn: apiCreateTransactionLog,
    networkMode: "offlineFirst",
    onSuccess() {
      paymentHistoryTransactionsQuery?.refetch();
    },
  });

  const paymentHistoryTransactionsQuery = useQuery({
    queryKey: ["payment-history-transaction", selected?.id],
    queryFn: () => apiGetTransaction(selected?.id || ""),
    enabled: !!selected?.id && open,
    refetchInterval: 1000 * 60,
  });
  const apiData = paymentHistoryTransactionsQuery?.data?.data;

  const transactionCreateMutation = useMutation({
    mutationFn: apiCreateTransaction,
    mutationKey: ["transaction-create"],
    onSuccess(data) {
      handleSelect(data?.data);

      createTransactionLog({
        comment: "Transaction created",
        transactionId: data?.data?.id,
        type: TransactionTypeLogType.Created,
        extraData: {
          ...data?.data,
        },
      });

      query?.refetch();
      toggle(false);
      // "customer-dashboard-transactions"
      queriesCacheClear({ search: "customer-dashboard-transactions" });
    },
  });

  const transactionUpdateMutation = useMutation({
    mutationFn: apiUpdateTransaction,
    mutationKey: ["transaction-update"],
    onSuccess(data) {
      const changedFields = diffDeep(data?.data, apiData);

      createTransactionLog({
        comment: "Transaction updated",
        transactionId: selected?.id,
        type: TransactionTypeLogType.Updated,
        extraData: {
          changedFields,
        },
      });

      paymentHistoryTransactionsQuery?.refetch();
      query?.refetch();
      queriesCacheClear({ search: "transaction" });
    },
    onError() {
      createTransactionLog({
        comment: `Transaction update failed`,
        transactionId: selected?.id,
        type: TransactionTypeLogType.Failed,
      });
    },
  });

  return (
    <Drawer
      title={
        <StyledFlex style={{ justifyContent: "space-between" }}>
          <FormattedMessage
            id={selected ? "common.editTitle" : "common.createTitle"}
            values={{ title: messages["common.paymentRequest"] as string }}
          />
          {!!selected?.id && (
            <ActionButton
              type="refresh"
              onClick={() => paymentHistoryTransactionsQuery?.refetch()}
              loading={paymentHistoryTransactionsQuery?.isLoading}
              disabled={paymentHistoryTransactionsQuery?.isLoading}
              size="small"
            />
          )}
        </StyledFlex>
      }
      open={open}
      onClose={() => toggle(false)}
      footer={""}
      width={800}
      className="payment-history-drawer"
    >
      <TransactionWorkflow
        transaction={apiData}
        isCreateMode={!selected}
        paymentHistoryTransactionsQuery={paymentHistoryTransactionsQuery}
        transactionCreateMutation={transactionCreateMutation}
        transactionUpdateMutation={transactionUpdateMutation}
        onTransactionSelect={handleSelect}
        createTransactionLog={createTransactionLog}
      />
    </Drawer>
  );
}

export default memo(PaymentHistoryDrawer, (prevProps, nextProps) => {
  return (
    prevProps.open === nextProps.open &&
    _.isEqual(prevProps.selected, nextProps.selected)
  );
});
